import React, { useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import Button from "../Button/Button";
import { menuPaperSx, menuItemSx, menuItemDestructiveSx } from "../internal/menuSx";
import "./SplitButton.css";

/**
 * Primary action + caret menu of secondary actions (PRD §5.4). Composes Button
 * so the primary half matches every other button exactly; the caret half shares
 * the same variant with a divider.
 *
 * Props:
 *   children:  primary label
 *   onClick:   primary action
 *   items:     [{ label, onClick, icon?, destructive?, disabled? }]
 *   variant:   "primary" | "secondary" | "ghost" | "danger" | "cta"  (default "primary")
 *   size:      "sm" | "md" | "lg"                    (default "md")
 *   startIcon: node on the primary half
 *   disabled:  disables both halves
 *   menuLabel: aria-label for the caret (default "More actions")
 *   className: extra class on the wrapper
 */
const Caret = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SplitButton = ({
  children,
  onClick,
  items = [],
  variant = "primary",
  size = "md",
  startIcon,
  disabled = false,
  menuLabel = "More actions",
  className = "",
}) => {
  const [anchor, setAnchor] = useState(null);

  return (
    <div className={`gx-split gx-split--${variant} ${className}`}>
      <Button
        variant={variant}
        size={size}
        startIcon={startIcon}
        onClick={onClick}
        disabled={disabled}
        className="gx-split-main"
      >
        {children}
      </Button>
      <Button
        variant={variant}
        size={size}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={disabled}
        className="gx-split-caret"
        aria-label={menuLabel}
        aria-haspopup="menu"
      >
        <Caret />
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: menuPaperSx } }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {items.map((item, i) => (
          <MenuItem
            key={i}
            disabled={item.disabled}
            onClick={() => {
              if (item.onClick) item.onClick();
              setAnchor(null);
            }}
            sx={item.destructive ? menuItemDestructiveSx : menuItemSx}
          >
            {item.icon && <span className="gx-split-item-icon">{item.icon}</span>}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default SplitButton;
